import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, TrendingUp, TrendingDown, Minus, BadgeCheck, AlertTriangle } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_STYLES = {
  Buy: 'text-[#2F6B3F] border-[#2F6B3F]',
  Hold: 'text-[#8B5A2B] border-[#8B5A2B]',
  Avoid: 'text-[#9B2C2C] border-[#9B2C2C]',
};

const formatUSD = (n) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
    : '—';

const Spark = ({ points, stroke = '#111' }) => {
  if (!points || points.length < 2) return null;
  const values = points.map((p) => p.price);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 120;
  const h = 36;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const path = values
    .map((v, i) => {
      const x = (i * step).toFixed(2);
      const y = (h - ((v - min) / range) * h).toFixed(2);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="overflow-visible">
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const trendIcon = (cagr) => {
  if (cagr > 1) return <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.75} />;
  if (cagr < -1) return <TrendingDown className="w-3.5 h-3.5" strokeWidth={1.75} />;
  return <Minus className="w-3.5 h-3.5" strokeWidth={1.75} />;
};

const trendColor = (cagr) => (cagr > 1 ? 'text-[#2F6B3F]' : cagr < -1 ? 'text-[#9B2C2C]' : 'text-[#555]');

export const InvestmentsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'cagr', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [idx, histories] = await Promise.all([
          axios.get(`${API}/investments`),
          Promise.resolve(null),
        ]);
        if (cancelled) return;
        // Load histories for each piece (parallel) so we can render sparklines
        const withHistory = await Promise.all(
          idx.data.pieces.map(async (p) => {
            try {
              const res = await axios.get(`${API}/investments/${p.slug}`);
              return { ...p, history: res.data.history || [] };
            } catch {
              return { ...p, history: [] };
            }
          })
        );
        if (!cancelled) setData({ summary: idx.data.summary, pieces: withHistory });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedPieces = useMemo(() => {
    if (!data?.pieces) return [];
    const arr = [...data.pieces];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      const va = a[key] ?? 0;
      const vb = b[key] ?? 0;
      if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return dir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [data, sort]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  const { summary, pieces } = data || { summary: {}, pieces: [] };

  return (
    <div data-testid="investments-page" className="bg-[#FAF9F6]">
      <SEO
        title="The Investment Index — Pre-Owned Luxury Jewelry Price Tracker"
        description="Live resale-value tracking for 12 iconic pre-owned luxury jewelry pieces. Editorial verdicts, 10-year CAGR, and our Buy/Hold/Avoid call for Cartier, Van Cleef, Tiffany, Bulgari, and Hermès."
        url="/investments"
        type="website"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'The Investment Index', url: '/investments' },
          ],
        }}
      />
      <SchemaOrg
        type="faqpage"
        data={{
          items: [
            {
              question: 'Which pre-owned jewelry pieces hold value best?',
              answer:
                'Hermès Collier de Chien, Van Cleef & Arpels Alhambra long necklaces, and Cartier Love bracelets have led the pre-owned index with positive appreciation over the last decade. Hermès fine jewelry trades above retail on the secondary market because of boutique waitlists.',
            },
            {
              question: 'Which pieces should I avoid as investments?',
              answer:
                'Tiffany T1 and smile pendants have depreciated meaningfully since launch. Harry Winston studs lose provenance premium with the first owner. These pieces wear well — they just do not hold value.',
            },
            {
              question: 'How often is the Investment Index updated?',
              answer:
                'Monthly. Numbers reflect mid-market resale pricing from Fashionphile, The RealReal, 1stDibs, and auction records from Sotheby\'s, Christie\'s, and Phillips.',
            },
          ],
        }}
      />

      {/* Cinematic Hero */}
      <section className="relative bg-[#111] text-white overflow-hidden">
        <div className="container-editorial pt-20 pb-16 lg:pt-28 lg:pb-20">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">The Investment Index</span>
          </nav>

          <p className="overline text-[#C69C6D] mb-6">Editor's Price Tracker</p>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-8">
            The <span className="italic">Investment</span> Index
          </h1>
          <p className="font-heading italic text-xl lg:text-2xl text-white/85 max-w-3xl mb-12">
            Which pre-owned pieces hold value — and which quietly bleed it. Updated monthly with ten-year resale data and our editorial call.
          </p>

          {/* Summary Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            <div className="bg-[#111] p-6">
              <p className="text-xs tracking-[0.15em] uppercase text-white/50 mb-2">Pieces Tracked</p>
              <p className="font-heading text-4xl font-light">{summary.count}</p>
            </div>
            <div className="bg-[#111] p-6">
              <p className="text-xs tracking-[0.15em] uppercase text-white/50 mb-2">Avg 10-Year CAGR</p>
              <p className="font-heading text-4xl font-light text-[#C69C6D]">{summary.avg_cagr?.toFixed(1)}%</p>
            </div>
            <div className="bg-[#111] p-6">
              <p className="text-xs tracking-[0.15em] uppercase text-white/50 mb-2">Buy Calls</p>
              <p className="font-heading text-4xl font-light">{summary.buy_count}</p>
            </div>
            <div className="bg-[#111] p-6">
              <p className="text-xs tracking-[0.15em] uppercase text-white/50 mb-2">Blue Chip</p>
              <p className="font-heading text-4xl font-light">{summary.blue_chip_count}</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Table */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-10 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">The Ranking</p>
              <h2 className="heading-editorial text-3xl lg:text-5xl">Every Piece, Every Year</h2>
            </div>
            <p className="hidden md:block text-xs tracking-wider text-[#888]">Tap any row to expand the editorial thesis</p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 text-xs tracking-[0.15em] uppercase text-[#888] border-b border-[#E5E0D8]">
              <button data-testid="sort-name" onClick={() => toggleSort('name')} className="col-span-4 text-left hover:text-[#111]">Piece</button>
              <button data-testid="sort-retail" onClick={() => toggleSort('retail_new')} className="col-span-1 text-right hover:text-[#111]">Retail</button>
              <button data-testid="sort-resale" onClick={() => toggleSort('current_resale')} className="col-span-1 text-right hover:text-[#111]">Resale</button>
              <button data-testid="sort-cagr" onClick={() => toggleSort('cagr')} className="col-span-1 text-right hover:text-[#111]">10Y CAGR</button>
              <div className="col-span-2 text-center">10-Year Trend</div>
              <div className="col-span-1">Rating</div>
              <div className="col-span-2 text-right">Verdict</div>
            </div>

            {sortedPieces.map((p) => (
              <Link
                key={p.slug}
                to={`/investments/${p.slug}`}
                data-testid={`investment-row-${p.slug}`}
                className="grid grid-cols-12 gap-4 px-6 py-5 items-center border-b border-[#E5E0D8] hover:bg-white transition-colors group"
              >
                <div className="col-span-4 flex items-center gap-4">
                  <div className="w-12 h-12 overflow-hidden bg-[#E5E0D8] flex-shrink-0">
                    <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading text-lg text-[#111] leading-tight truncate group-hover:text-[#C69C6D] transition-colors">{p.name}</p>
                    <p className="text-xs text-[#888] tracking-wide truncate">{p.designer} · {p.material}</p>
                  </div>
                </div>
                <div className="col-span-1 text-right text-sm text-[#555]">{formatUSD(p.retail_new)}</div>
                <div className="col-span-1 text-right text-sm text-[#111] font-medium">{formatUSD(p.current_resale)}</div>
                <div className={`col-span-1 text-right text-sm font-medium inline-flex items-center justify-end gap-1 ${trendColor(p.cagr)}`}>
                  {trendIcon(p.cagr)}
                  <span>{p.cagr > 0 ? '+' : ''}{p.cagr.toFixed(1)}%</span>
                </div>
                <div className="col-span-2 flex justify-center">
                  <Spark points={p.history} stroke={p.cagr < 0 ? '#9B2C2C' : '#111'} />
                </div>
                <div className="col-span-1 text-[11px] tracking-wider uppercase text-[#555]">{p.rating}</div>
                <div className="col-span-2 text-right">
                  <span className={`inline-block text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 ${VERDICT_STYLES[p.verdict] || 'text-[#111] border-[#111]'}`}>
                    {p.verdict}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {sortedPieces.map((p) => (
              <Link
                key={p.slug}
                to={`/investments/${p.slug}`}
                data-testid={`investment-card-${p.slug}`}
                className="block bg-white border border-[#E5E0D8] p-5"
              >
                <div className="flex gap-4 mb-4">
                  <div className="w-20 h-20 overflow-hidden bg-[#E5E0D8] flex-shrink-0">
                    <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-lg text-[#111] leading-tight">{p.name}</p>
                    <p className="text-xs text-[#888] mt-1">{p.designer}</p>
                    <span className={`inline-block mt-2 text-[10px] tracking-[0.2em] uppercase border px-2.5 py-1 ${VERDICT_STYLES[p.verdict] || 'text-[#111] border-[#111]'}`}>
                      {p.verdict} · {p.rating}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-[#E5E0D8]">
                  <div>
                    <p className="text-[10px] tracking-wider uppercase text-[#888]">Resale</p>
                    <p className="font-heading text-xl text-[#111]">{formatUSD(p.current_resale)}</p>
                  </div>
                  <div className={`text-right ${trendColor(p.cagr)}`}>
                    <p className="text-[10px] tracking-wider uppercase text-[#888]">10Y CAGR</p>
                    <p className="font-heading text-xl">{p.cagr > 0 ? '+' : ''}{p.cagr.toFixed(1)}%</p>
                  </div>
                  <Spark points={p.history} stroke={p.cagr < 0 ? '#9B2C2C' : '#111'} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer strip */}
      <section className="bg-[#F3EFE6] py-8 border-y border-[#E5E0D8]">
        <div className="container-editorial flex items-start gap-4 text-xs text-[#555] leading-relaxed">
          <AlertTriangle className="w-4 h-4 text-[#8B5A2B] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p>
            The Investment Index is editorial commentary, not financial advice. Price observations reflect mid-market resale pricing sourced from Fashionphile, The RealReal, 1stDibs, and auction records. Individual piece values vary by condition, provenance, and completeness of box &amp; papers.
            Always verify before purchase — <Link to="/affiliate-disclosure" className="text-[#C69C6D] hover:text-[#111]">see our disclosure</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#111] text-white py-20">
        <div className="container-editorial text-center">
          <BadgeCheck className="w-8 h-8 text-[#C69C6D] mx-auto mb-6" strokeWidth={1.25} />
          <p className="overline text-[#C69C6D] mb-4">Next</p>
          <h2 className="heading-editorial text-3xl lg:text-5xl text-white mb-6">Shop the Buy-Rated Pieces</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-10">See curated fresh inventory from our vetted affiliate partners — updated daily.</p>
          <Link to="/drops" className="btn-editorial btn-editorial-inverse inline-flex items-center gap-3">
            Browse Current Drops <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InvestmentsPage;
